import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';
import ProductHeading from 'components/pages/product/product_heading';
import ProductActions from 'components/pages/product/product_actions';
import ProductAlternatives from 'components/pages/product/product_alternatives';
import ProductAddOn, {
  NO_ADD_ON_VALUE,
} from 'components/pages/product/product_add_on';
import ShippingBubble from 'components/pages/product/shipping_bubble';
import SubtotalBox from 'components/pages/product/subtotal_box';
import SoftwareAddOn from 'components/pages/product/product_with_software_add_on/software_add_on';
import {
  fetchSubscriptionState,
  fetchTrialEndDate,
  SOFTWARE_SUBSCRIPTION_STATES,
  pullSoftwareFromAddOns,
} from 'utilities/software';
import SoftwareAddOnBanner from 'components/pages/product/product_with_software_add_on/software_add_on_banner';
import RichTextContent from 'components/common/contentful/rich_text_content';

import { useTranslation } from 'react-i18next';
import { trackEvent, trackCdp } from 'utilities/tracking';
import {
  extractSoftwareAddOns,
  sortProductAddOnSections,
  PRE_SELECTED_ADD_ONS,
} from 'components/pages/product/product_with_software_add_on/utils';
import {
  TRACKING_EVENT_TYPES,
  getAddOnByVariantId,
  getAlternativeUrl,
  setFooterDisclaimer,
} from 'components/pages/product/helpers';
import { updateCtaSubnav, updateCurrentUrl } from 'utilities/nav';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import InfoBubble from 'components/common/info_bubble';
import TagIcon from '@square-icons/react/16/UI/tag';
import { useProductOptionStyles } from '../product_options';
import ProductAddOnSkeleton from '../product_add_on_skeleton';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';

const HardwareWithSoftwareAddOnOptions = ({
  alternatives,
  currentAltMetadata,
  setFinancingModalOpen,
}) => {
  const {
    currentProduct,
    setCurrentProduct,
    selection,
    setSelection,
    addOns,
    isGetAddOnsLoading: isAddOnsLoading,
    isGetAddOnsError: isAddOnsLoadingError,
    cart,
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading,
    scrollToRef,
    promoEligibility,
  } = useProductContext();

  const {
    base_product_key: baseProductKey,
    slug,
    name: productName,
    variant_token: variantToken,
    tracking_data: trackingData,
    shipping: {
      out_of_stock: outOfStock,
      no_ship: noShip,
      ship_quote: shipQuote,
      expedited_available: expeditedAvailable,
    },
    description,
    promotion_banner: promoBanner,
    add_ons_category_count: addOnsCategoryCount,
  } = currentProduct;

  const { number: orderNumber, item_count: itemCount, subtotal } = cart || {};

  const { t, i18n } = useTranslation();
  const classes = useProductOptionStyles();
  const theme = useTheme();

  // Track the last product slug we pre-selected for to avoid re-running
  const preSelectedForSlugRef = useRef(null);

  // this useEffect is to update selections to include the requested add-ons upon page load
  // uses a mapping of product key to pre-selected add-ons, which is defined in the utils file
  // https://www.figma.com/design/pd4ELXLMyFOnE1SUT5vAb3/Shop---Kiosk-Launch?node-id=584-36132&t=gXIJZWecEJrFDIAZ-0
  useEffect(() => {
    if (isAddOnsLoading) return;
    // skip preselecting if we already pre-selected for this product
    if (preSelectedForSlugRef.current === slug) return;

    const preSelectedCategories = PRE_SELECTED_ADD_ONS[baseProductKey];

    // Build the new selection with pre-selected add-ons
    const newAddOns = {};
    if (preSelectedCategories && addOns && Object.keys(addOns).length > 0) {
      preSelectedCategories.forEach((category) => {
        const categoryAddOns = addOns[category];
        if (categoryAddOns && categoryAddOns.length > 0) {
          newAddOns[category] = categoryAddOns[0].variant_id.toString();
        }
      });
    }

    setSelection({ quantity: 1, addOns: newAddOns });
    preSelectedForSlugRef.current = slug;
  }, [slug, baseProductKey, addOns, isAddOnsLoading, setSelection]);

  useEffect(() => {
    if (!addOns || Object.keys(addOns).length === 0) return;

    const software = pullSoftwareFromAddOns(addOns);

    software.forEach((addOn) => {
      const key = `productPage.software.disclaimer.${addOn.base_product_key}`;
      if (i18n.exists(key)) {
        setFooterDisclaimer(t(key));
      }
    });
  }, [currentProduct]);

  const onAlternativeSelected = (value) => {
    if (value === null) {
      return;
    }

    // Track alternative selected events
    const selectedAlternative = alternatives.find((alt) => alt.slug === value);
    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });

    // Update current window path to selected alternative slug
    window.history.replaceState(
      {},
      null,
      getAlternativeUrl(selectedAlternative.slug),
    );

    // Update PublicWebNav current URL & CTA Subnav
    updateCurrentUrl(getAlternativeUrl(selectedAlternative.slug));
    updateCtaSubnav(selectedAlternative.cta_subnav);

    setCurrentProduct(selectedAlternative);
  };

  const onAddOnSelected = ({
    category: addOnCategory,
    newVariantId,
    choice,
  }) => {
    if (newVariantId === null) {
      return;
    }

    if (newVariantId === NO_ADD_ON_VALUE) {
      setSelection((prevSelection) => {
        const newAddOns = { ...prevSelection.addOns };
        delete newAddOns[addOnCategory];
        return { ...prevSelection, addOns: newAddOns };
      });
      return;
    } else {
      setSelection((prevSelection) => {
        const newAddOns = { ...prevSelection.addOns };
        newAddOns[addOnCategory] = newVariantId;
        return { ...prevSelection, addOns: newAddOns };
      });
    }

    const trackingData = getAddOnByVariantId(
      addOns,
      newVariantId,
    ).tracking_data;

    trackEvent({
      name: 'Add On',
      value: 'Change',
      params: {
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });

    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAddOn,
      params: {
        feature: 'shop',
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });
  };

  const onAddToCartClick = () => {
    onUpdateItemQuantity({
      variantToken,
      productName,
      quantity: selection.quantity,
      addOnIds: Object.values(selection.addOns),
      trackingData,
    });
  };

  const renderAddOns = () => {
    if (
      isAddOnsLoading ||
      // The request finished loading, but the data hasn't populated yet
      (!isAddOnsLoading &&
        !isAddOnsLoadingError &&
        Object.keys(addOns).length === 0 &&
        addOnsCategoryCount > 0)
    ) {
      return (
        <div
          style={{
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
          }}
        >
          {Array.from({ length: addOnsCategoryCount }, (_, index) => (
            <div key={index} style={{ marginBottom: theme.spacing(2) }}>
              <ProductAddOnSkeleton />
            </div>
          ))}
        </div>
      );
    }

    const { softwareAddOnSections, hardwareAddOnSections } =
      sortProductAddOnSections(addOns);

    const addOnComponents = [];

    if (softwareAddOnSections.length > 0) {
      softwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <SoftwareAddOn
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
          />,
        );
      });
    }

    if (hardwareAddOnSections.length > 0) {
      hardwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <ProductAddOn
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
            currentProduct={currentProduct}
          />,
        );
      });
    }

    return <div className={classes.fadeIn}>{addOnComponents}</div>;
  };

  const renderSoftwareAddOnBanners = () => {
    const softwareAddOns = extractSoftwareAddOns(addOns);

    if (softwareAddOns.length === 0) {
      return null;
    }

    return softwareAddOns.map((addOn) => {
      const subscriptionState = fetchSubscriptionState(addOn);
      const trialEndDate = fetchTrialEndDate(addOn);

      if (subscriptionState === SOFTWARE_SUBSCRIPTION_STATES.NOT_SUBSCRIBED) {
        return null;
      }

      return (
        <SoftwareAddOnBanner
          key={addOn.variant_id}
          softwareBaseProductKey={addOn.base_product_key}
          subscriptionState={subscriptionState}
          softwareName={addOn.name}
          trialEndDate={trialEndDate}
        />
      );
    });
  };

  const renderPromotionBanner = () => (
    <>
      <ProductPromotionBanner {...promoEligibility} slug={slug} />
      {promoBanner && (
        <InfoBubble icon={<TagIcon />}>
          <RichTextContent content={promoBanner} className="medium-20" />
        </InfoBubble>
      )}
    </>
  );

  return (
    <>
      <ProductHeading
        currentProduct={currentProduct}
        onFinancingTriggerClick={() => setFinancingModalOpen(true)}
      />
      <RichTextContent content={description} />
      {renderPromotionBanner()}
      {(Object.keys(addOns).length > 0 || alternatives.length > 0) && (
        <h2
          className={classes.fadeIn}
          style={{ marginTop: theme.spacing(3.5) }}
        >
          {t('productPage.hardwareWithSoftwareHeader')}
        </h2>
      )}
      {renderSoftwareAddOnBanners()}
      <div className={classes.fadeIn}>
        <ProductAlternatives
          currentProduct={currentProduct}
          alternatives={alternatives}
          currentAltMetadata={currentAltMetadata}
          onAlternativeSelected={onAlternativeSelected}
        />
      </div>
      {renderAddOns()}
      <ProductActions
        currentProduct={currentProduct}
        addOns={addOns}
        selection={selection}
        setSelection={setSelection}
        requesting={isUpdateItemQuantityLoading}
        onAddToCartClick={onAddToCartClick}
      />
      <div
        style={{
          marginTop: theme.spacing(2.5),
          color: 'grey',
          fontSize: '14px',
          textAlign: 'center',
        }}
      >
        {t('productPage.software.softwareAddToCartDisclaimer')}
      </div>
      {!(outOfStock || noShip) && (
        <div
          data-testid="shipping-bubble-container"
          style={{ marginTop: theme.spacing(3.5) }}
        >
          <ShippingBubble
            quote={shipQuote}
            expeditedAvailable={expeditedAvailable}
          />
        </div>
      )}
      <div style={{ marginTop: theme.spacing(3.5) }} />
      <div ref={scrollToRef}>
        <SubtotalBox
          product={currentProduct}
          cart={cart}
          subtotal={subtotal}
          itemCount={itemCount}
        />
      </div>
    </>
  );
};

export default HardwareWithSoftwareAddOnOptions;
